export default function MurfreesboroAcceptanceFacilitiesSection() {
  const facilities = [
    {
      id: "mboro-1",
      name: "Murfreesboro Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "121 Academy St",
      city: "Murfreesboro",
      state: "TN",
      zip: "37130",
      phone: "(615) 893-6474",
      hours: "Mon–Fri 8:30 AM – 5:00 PM (appointment required)",
      distance: "Downtown Murfreesboro — near the Square",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location",
      notes: "Primary passport acceptance facility for Rutherford County. Photo services available. Appointments fill quickly before MTSU study abroad deadlines and spring break season — book 2–3 weeks ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "mboro-2",
      name: "Rutherford County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "319 N Maple St",
      city: "Murfreesboro",
      state: "TN",
      zip: "37130",
      phone: "(615) 898-7730",
      hours: "Mon–Fri 8:00 AM – 4:30 PM",
      distance: "Downtown Murfreesboro — Rutherford County Courthouse",
      website: "https://www.rutherfordcountytn.gov/clerk",
      websiteLabel: "Rutherford County Clerk",
      notes: "Accepts new passport applications and provides notarizations. Convenient for MTSU students and Murfreesboro families. No photo services on-site — get photos taken before your visit.",
      icon: "ri-government-line",
    },
    {
      id: "mboro-3",
      name: "Atlanta Passport Agency (Regional)",
      type: "Regional Passport Agency",
      address: "805 Broadway",
      city: "Nashville",
      state: "TN",
      zip: "37203",
      phone: "(877) 487-2778",
      hours: "Mon–Fri 8:00 AM – 3:00 PM (by appointment only)",
      distance: "35 miles from Murfreesboro via I-24",
      website: "https://travel.state.gov/content/travel/en/passports/need-passport/agency.html",
      websiteLabel: "State Dept. Info",
      notes: "Regional passport agency for Middle Tennessee. Life-or-death emergencies only — appointments frequently booked 2+ weeks out. Not suitable for MTSU study abroad deadlines or standard expedited needs.",
      icon: "ri-bank-line",
    },
  ];

  return (
    <section id="acceptance-facilities" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Facilities</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Passport Acceptance Facilities Near Murfreesboro
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            First-time applicants and minors must visit an acceptance clerk in person. These Rutherford County facilities serve Murfreesboro residents — though most travelers using Fast Passport Center ship documents directly to us after the clerk visit.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {facilities.map((f) => (
            <div key={f.id} className="bg-white rounded-xl p-7 border border-gray-200 flex flex-col">
              <div className="flex items-start justify-between mb-4">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0">
                  <i className={`${f.icon} text-[#1A365D] text-xl`}></i>
                </div>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#EEF2F8] text-[#1A365D] whitespace-nowrap">
                  {f.type === "Regional Passport Agency" ? (
                    <><i className="ri-alert-line mr-1"></i>Regional Agency</>
                  ) : f.type}
                </span>
              </div>
              <h3 className="font-serif text-lg font-bold text-[#1A365D] mb-1">{f.name}</h3>
              <p className="text-gray-400 text-xs mb-2">{f.address}, {f.city}, {f.state} {f.zip}</p>
              <div className="flex items-center gap-1.5 text-xs text-[#F6C344] font-semibold mb-3">
                <i className="ri-road-map-line"></i>
                <span>{f.distance}</span>
              </div>
              <div className="space-y-2 text-sm text-gray-600 mb-4 flex-1">
                <div className="flex items-center gap-2">
                  <i className="ri-time-line text-gray-400 text-sm flex-shrink-0"></i>
                  <span>{f.hours}</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-phone-line text-gray-400 text-sm flex-shrink-0"></i>
                  <a href={`tel:${f.phone.replace(/[^0-9]/g, "")}`} className="hover:text-[#1A365D] transition-colors">{f.phone}</a>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-global-line text-gray-400 text-sm flex-shrink-0 mt-0.5"></i>
                  <a href={f.website} target="_blank" rel="noopener noreferrer nofollow" className="hover:text-[#1A365D] transition-colors text-xs leading-snug">{f.websiteLabel}</a>
                </div>
              </div>
              <p className="text-gray-400 text-xs leading-relaxed border-t pt-3">{f.notes}</p>
            </div>
          ))}
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-100 flex-shrink-0">
              <i className="ri-alert-line text-amber-600 text-lg"></i>
            </div>
            <div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">Important: Do Not Submit Through the Clerk Alone</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                If you sign up with Fast Passport Center first, we review your documents before your visit, provide your sealed courier envelope, and ensure everything is correct. Once documents are submitted standard through the clerk without our courier channel, we cannot intervene — standard processing takes 6–11 weeks. For MTSU study abroad deadlines and Nissan Smyrna business travel, that difference matters.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[#1A365D] rounded-xl p-8 text-center">
          <h3 className="font-serif text-xl font-bold text-white mb-3">Last Chance Before You Go to the Clerk</h3>
          <p className="text-white/70 text-sm max-w-2xl mx-auto leading-relaxed mb-6">
            MTSU students missing study abroad deadlines and Rutherford County families with upcoming international trips — start with us first. Visit the clerk with our sealed courier envelope and skip the 11-week standard processing queue.
          </p>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="inline-flex items-center gap-2 bg-[#F6C344] text-[#1A365D] font-semibold px-8 py-3 rounded-lg hover:bg-[#e5b33f] transition-all whitespace-nowrap"
          >
            Start With Fast Passport Center <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </div>
    </section>
  );
}